import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Package, PackageOpen, PackageCheck, ExternalLink, LogIn, ScanLine } from "lucide-react";
import QRScannerDialog from "@/components/QRScannerDialog";

const BBS_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_production-ops-23/artifacts/hm9k398i_Screenshot%202019-07-29%20at%201.08.27%20PM.png";
const OPSIQ_LOCKUP = "https://customer-assets-lxgj4vgw.emergentagent.net/job_production-ops-23/artifacts/m4d6gaj0_ops-iq-lockup-1200.png";

const TYPE_LABEL = {
  buyer_original: "Buyer's Original",
  pp_gold_seal: "PP / Gold Seal",
};
const STATUS_LABEL = {
  in_stores: "In Stores",
  issued: "Issued out",
  lost: "Lost",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function SampleScan() {
  const { qrId } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [scannerOpen, setScannerOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // withCredentials so an existing session upgrades to full view; falls back to public if not logged in
        const { data } = await axios.get(`${API_BASE}/samples/scan/${qrId}`, { withCredentials: true });
        setState({ loading: false, error: null, data });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Sample not found", data: null });
      }
    })();
  }, [qrId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950" data-testid="sample-scan-page">
      <header className="w-full border-b bg-white/70 dark:bg-slate-900/70 backdrop-blur">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 sm:px-6 h-16">
          <img src={BBS_LOGO} alt="BBS Global" className="h-8 w-auto object-contain" />
          <img src={OPSIQ_LOCKUP} alt="Ops IQ" className="h-6 w-auto object-contain" />
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setScannerOpen(true)} data-testid="scan-next-btn">
              <ScanLine className="h-4 w-4 mr-1.5" /> Scan next
            </Button>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold hidden sm:block">Sample tag</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {state.loading && <div className="text-center text-sm text-slate-500 py-16">Loading…</div>}

        {state.error && (
          <Card className="p-8 text-center">
            <div className="text-lg font-heading font-bold text-slate-900 dark:text-slate-100">Sample not found</div>
            <div className="text-sm text-slate-500 mt-1">The QR code <span className="font-mono">{qrId}</span> doesn't match any registered sample.</div>
          </Card>
        )}

        {state.data && (
          <Card className="p-6" data-testid="scan-card">
            <div className="flex items-center gap-2 mb-4">
              {state.data.sample.sample_type === "buyer_original" ? (
                <PackageOpen className="h-5 w-5 text-violet-700" />
              ) : (
                <PackageCheck className="h-5 w-5 text-amber-700" />
              )}
              <Badge variant="outline">{TYPE_LABEL[state.data.sample.sample_type] || state.data.sample.sample_type}</Badge>
              {state.data.authed && state.data.sample.status && (
                <Badge className={state.data.sample.status === "issued" ? "bg-blue-600" : state.data.sample.status === "lost" ? "bg-rose-600" : "bg-emerald-600"}>{STATUS_LABEL[state.data.sample.status]}</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 sm:gap-6 items-start">
              <div className="text-center sm:text-left space-y-1">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Buyer</div>
                <div className="text-lg font-heading font-bold">{state.data.sample.buyer_name}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-6">
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Buyer Style</div>
                  <div className="text-[15px] font-semibold mt-0.5">{state.data.sample.buyer_style || "—"}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">BBS Style</div>
                  <div className="text-[15px] font-semibold mt-0.5">{state.data.sample.bbs_style || "—"}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Size</div>
                  <div className="text-[15px] font-semibold mt-0.5">{state.data.sample.size || "—"}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Piece</div>
                  <div className="text-[15px] font-semibold mt-0.5">{state.data.sample.seq_label} / {state.data.sample.no_of_pcs}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">QR</div>
                  <div className="text-[13px] font-mono mt-0.5">{state.data.sample.qr_id}</div>
                </div>
              </div>
            </div>

            {state.data.authed ? (
              <>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="p-3 rounded border">
                    <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Received/made</div>
                    <div className="text-[13px] font-semibold mt-0.5">{fmtDate(state.data.sample.date_of_receipt_or_make)}</div>
                  </div>
                  <div className="p-3 rounded border">
                    <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Registered on</div>
                    <div className="text-[13px] font-semibold mt-0.5">{fmtDate(state.data.sample.registration_date)}</div>
                  </div>
                  <div className="p-3 rounded border">
                    <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Location</div>
                    <div className="text-[13px] font-semibold mt-0.5">{state.data.sample.current_location}</div>
                  </div>
                  <div className="p-3 rounded border">
                    <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Group pieces</div>
                    <div className="text-[13px] font-semibold mt-0.5">{state.data.group_count || 1}</div>
                  </div>
                </div>

                {state.data.open_request && (
                  <div className="mt-4 rounded border border-blue-200 bg-blue-50 dark:bg-blue-950/30 p-3">
                    <div className="text-[11px] uppercase tracking-widest text-blue-800 font-semibold">Open request</div>
                    <div className="text-[13px] mt-1"><b>{state.data.open_request.requested_by_name}</b> — expected back {fmtDate(state.data.open_request.expected_return_date)}</div>
                    <div className="text-[12px] text-blue-900/80">{state.data.open_request.reason}</div>
                  </div>
                )}

                <div className="mt-6">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-2">Transactions</div>
                  {state.data.transactions.length === 0 ? (
                    <div className="text-[13px] text-slate-500">No transactions.</div>
                  ) : (
                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                      {state.data.transactions.map((t) => (
                        <div key={t.id} className="text-[12px] px-2 py-1.5 rounded border">
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="capitalize text-[10.5px]">{t.action}</Badge>
                            <span className="text-slate-500">{t.from_location || "—"} → {t.to_location || "—"}</span>
                          </div>
                          {t.note && <div className="text-slate-600 mt-0.5">{t.note}</div>}
                          <div className="text-slate-400 text-[10.5px] mt-0.5">{fmtDateTime(t.at)} · {t.actor}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t text-right">
                  <Link to="/" className="inline-flex items-center gap-1 text-[13px] text-brand hover:underline">
                    Open Ops IQ <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </>
            ) : (
              <div className="mt-6 pt-4 border-t">
                <div className="rounded-md border border-slate-200 bg-slate-50 dark:bg-white/5 p-4 text-center">
                  <Package className="h-6 w-6 text-slate-500 inline-block" />
                  <div className="text-[13px] text-slate-700 dark:text-slate-300 mt-1">Log in to see location, history and open requests for this sample.</div>
                  <Link to={`/login?next=${encodeURIComponent(`/samples/scan/${qrId}`)}`} className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-white bg-brand hover:bg-brand-dark px-3 py-1.5 rounded">
                    <LogIn className="h-4 w-4" /> Sign in to Ops IQ
                  </Link>
                </div>
              </div>
            )}
          </Card>
        )}
      </main>

      <QRScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={(newId) => {
          setScannerOpen(false);
          if (newId !== qrId) nav(`/samples/scan/${newId}`);
        }}
      />
    </div>
  );
}
